import { useState, useCallback } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// Re-export pure label maps from the dependency-free module so consumers only
// need to import from one place.
export type {
  CustomerStatus,
  IntentLevel,
  FollowupType,
  SourceType,
  ChannelPartnerStatus,
  HandoffStatus,
} from './crmLabels';
export {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUS_LIST,
  INTENT_LEVEL_LABELS,
  FOLLOWUP_TYPE_LABELS,
  SOURCE_TYPE_LABELS,
  CHANNEL_PARTNER_STATUS_LABELS,
  HANDOFF_STATUS_LABELS,
} from './crmLabels';

import type {
  CustomerStatus,
  IntentLevel,
  ChannelPartnerStatus,
} from './crmLabels';

// Initialize API client with empty baseUrl since paths already include /api/v1.
// CRM 客户/渠道端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ─── DTO type aliases ────────────────────────────────────────────────────────

export type CustomerDto = ApiComponents['schemas']['CustomerDto'];
export type CreateCustomerDto = ApiComponents['schemas']['CreateCustomerDto'];
export type UpdateCustomerDto = ApiComponents['schemas']['UpdateCustomerDto'];
export type TransitionCustomerStageDto = ApiComponents['schemas']['TransitionCustomerStageDto'];

export type FollowupDto = ApiComponents['schemas']['FollowupDto'];
export type CreateFollowupDto = ApiComponents['schemas']['CreateFollowupDto'];
export type UpdateFollowupDto = ApiComponents['schemas']['UpdateFollowupDto'];

export type HandoffDto = ApiComponents['schemas']['HandoffDto'];
export type CreateHandoffDto = ApiComponents['schemas']['CreateHandoffDto'];
export type ConfirmHandoffDto = ApiComponents['schemas']['ConfirmHandoffDto'];
export type RejectHandoffDto = ApiComponents['schemas']['RejectHandoffDto'];

export type SourceDto = ApiComponents['schemas']['SourceDto'];
export type CreateSourceDto = ApiComponents['schemas']['CreateSourceDto'];

export type AssignmentDto = ApiComponents['schemas']['AssignmentDto'];
export type UpsertAssignmentDto = ApiComponents['schemas']['UpsertAssignmentDto'];

export type TagDto = ApiComponents['schemas']['TagDto'];
export type AddTagDto = ApiComponents['schemas']['AddTagDto'];

export type ChannelPartnerDto = ApiComponents['schemas']['ChannelPartnerDto'];
export type CreateChannelPartnerDto = ApiComponents['schemas']['CreateChannelPartnerDto'];
export type UpdateChannelPartnerDto = ApiComponents['schemas']['UpdateChannelPartnerDto'];

// ─── Shared error types and helpers ──────────────────────────────────────────

export interface CrmApiError {
  message: string;
  code?: string;
  /** HTTP status code, e.g. 409 for conflict. Present when the server returned
   *  a structured error response. */
  status?: number;
}

/**
 * Convert a thrown error (from the openapi-fetch middleware) into CrmApiError.
 * The middleware throws ApiError for non-2xx responses, so status is available.
 */
function thrownToApiError(err: unknown): CrmApiError {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED', status: 401 };
    }
    if (err.status === 403) {
      return { message: '当前角色没有 CRM 模块访问权限', code: 'FORBIDDEN', status: 403 };
    }
    return { message: err.message, code: err.code, status: err.status };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

/**
 * Map the `error` field returned by openapi-fetch into CrmApiError.
 *
 * openapi-fetch returns `{ data, error }` where `error` is the parsed error
 * body (type varies per path). The middleware already throws ApiError for
 * non-2xx, so in practice `apiErr` here is only set for network/parse issues
 * that don't throw. We extract what we can and fall back to a generic message.
 *
 * IMPORTANT: We must NOT use `any` or `as any`. The error body type from
 * openapi-fetch is `never` when the schema has no error response body defined,
 * or a specific schema type otherwise. We accept `unknown` and narrow safely.
 */
function responseErrToApiError(apiErr: unknown, fallbackMessage: string): CrmApiError {
  if (apiErr === null || apiErr === undefined) {
    return { message: fallbackMessage };
  }
  if (typeof apiErr === 'object') {
    const e = apiErr as Record<string, unknown>;
    const message =
      typeof e['message'] === 'string'
        ? e['message']
        : typeof e['error'] === 'string'
          ? e['error']
          : fallbackMessage;
    const code = typeof e['code'] === 'string' ? e['code'] : undefined;
    const status = typeof e['status'] === 'number' ? e['status'] : undefined;
    return { message, code, status };
  }
  return { message: fallbackMessage };
}

// ============================================================================
// Customer Hooks
// ============================================================================

/**
 * Paginated list response returned by CRM list endpoints. The backend has
 * always answered `/api/v1/crm/customers` (and similar) with `{ data, total }`;
 * the OpenAPI contract was corrected in Issue #238 to match that reality so
 * the frontend never again calls `.map` on what is actually an object.
 */
export interface CrmListResult<T> {
  readonly data: readonly T[];
  readonly total: number;
}

/**
 * Runtime guard for CRM paginated list responses. Issue #238 audit follow-up:
 * the typed client only validates against the OpenAPI schema at compile time —
 * a misbehaving gateway, reverse proxy, or partial backend rollout can still
 * send HTTP 200 with a body that does not match `{ data: T[], total: number }`.
 * Without this guard the consuming pages (`/crm/customers`, `/channel-roi`,
 * `/crm/channel-partners`) would set `undefined` into list state and crash with
 * `l.map is not a function` inside the global ErrorBoundary.
 *
 * Returns a discriminated union so callers can surface a controlled
 * `CrmApiError` instead of writing `undefined` into render state.
 */
export function ensureCrmListShape<T>(
  body: unknown,
  fallback: { readonly message: string; readonly code: string },
):
  | { readonly ok: true; readonly value: CrmListResult<T> }
  | { readonly ok: false; readonly error: CrmApiError } {
  // openapi-fetch returns `undefined` for empty/204 responses; treat as empty
  // list so navigating to a fresh org does not flash a controlled error.
  if (body === undefined) {
    return { ok: true, value: { data: [], total: 0 } };
  }
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return { ok: false, error: { message: fallback.message, code: fallback.code } };
  }
  const record = body as { readonly data?: unknown; readonly total?: unknown };
  if (!Array.isArray(record.data) || typeof record.total !== 'number') {
    return { ok: false, error: { message: fallback.message, code: fallback.code } };
  }
  return {
    ok: true,
    value: {
      data: record.data as readonly T[],
      total: record.total,
    },
  };
}

/**
 * Runtime guard for CRM sub-resource list responses that the consuming pages
 * render directly as arrays (followups, handoffs, sources, tags). Issue #341
 * root cause: the backend controllers return `{ data, total }` wrappers, but
 * the typed client previously declared the response as a bare array. The
 * hooks fed the raw body into React state and `CustomerDetailPage` then
 * called `.map(...)`, which threw `<x>.map is not a function` and triggered
 * the global ErrorBoundary "页面发生异常".
 *
 * To survive both partial backend rollouts and a misbehaving gateway, this
 * helper accepts either shape and always hands back a plain array:
 *
 *   - `undefined`        → `[]` (openapi-fetch surfaces empty/204 bodies as
 *                          `undefined`; treat that as an empty list rather
 *                          than a controlled error)
 *   - `T[]`              → the same array (kept for forward-compat in case a
 *                          future contract change inlines the array)
 *   - `{ data: T[], … }` → `data` (the documented backend wrapper)
 *   - anything else      → `{ ok: false, error }` so the hook can surface a
 *                          controlled error instead of writing `undefined`
 *                          into render state
 */
export function ensureCrmListArray<T>(
  body: unknown,
  fallback: { readonly message: string; readonly code: string },
):
  | { readonly ok: true; readonly value: readonly T[] }
  | { readonly ok: false; readonly error: CrmApiError } {
  if (body === undefined) {
    return { ok: true, value: [] };
  }
  if (Array.isArray(body)) {
    return { ok: true, value: body as readonly T[] };
  }
  if (body !== null && typeof body === 'object') {
    const record = body as { readonly data?: unknown };
    if (Array.isArray(record.data)) {
      return { ok: true, value: record.data as readonly T[] };
    }
  }
  return { ok: false, error: { message: fallback.message, code: fallback.code } };
}

export function useCustomerList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      status?: CustomerStatus;
      intentLevel?: IntentLevel;
      assignedUserId?: string;
    }): Promise<CrmListResult<CustomerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/customers', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取客户列表失败'));
          return null;
        }
        const guarded = ensureCrmListShape<CustomerDto>(data, {
          message: '客户列表响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useCustomerDetail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(async (id: string): Promise<Readonly<CustomerDto> | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/customers/{id}', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError(responseErrToApiError(apiErr, '获取客户详情失败'));
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, fetch };
}

export function useCustomerCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (body: CreateCustomerDto): Promise<Readonly<CustomerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/customers', { body });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建客户失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

export function useCustomerUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const update = useCallback(
    async (id: string, body: UpdateCustomerDto): Promise<Readonly<CustomerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/customers/{id}', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '更新客户失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, update };
}

export function useCustomerStageTransition() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const transition = useCallback(
    async (id: string, body: TransitionCustomerStageDto): Promise<Readonly<CustomerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/customers/{id}/status', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '阶段变更失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, transition };
}

// ============================================================================
// Followup Hooks
// ============================================================================

export function useFollowupList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      customerId?: string;
      page?: number;
      limit?: number;
    }): Promise<readonly FollowupDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/followups', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取跟进记录失败'));
          return null;
        }
        // Issue #341: backend answers with `{ data, total }`; unwrap to an
        // array before handing back to the page so `.map(...)` cannot throw.
        const guarded = ensureCrmListArray<FollowupDto>(data, {
          message: '跟进记录响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useFollowupCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (body: CreateFollowupDto): Promise<Readonly<FollowupDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/followups', { body });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建跟进记录失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

export function useFollowupUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const update = useCallback(
    async (id: string, body: UpdateFollowupDto): Promise<Readonly<FollowupDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/followups/{id}', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '更新跟进记录失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, update };
}

// ============================================================================
// Handoff Hooks
// ============================================================================

export function useHandoffList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      customerId?: string;
      page?: number;
      limit?: number;
    }): Promise<readonly HandoffDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/handoffs', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取移交记录失败'));
          return null;
        }
        // Issue #341: see ensureCrmListArray for the wrapper/array contract.
        const guarded = ensureCrmListArray<HandoffDto>(data, {
          message: '移交记录响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useHandoffCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (body: CreateHandoffDto): Promise<Readonly<HandoffDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/handoffs', { body });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建移交申请失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

export function useHandoffConfirm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const confirm = useCallback(
    async (id: string, body: ConfirmHandoffDto): Promise<Readonly<HandoffDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/handoffs/{id}/confirm', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '确认移交失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, confirm };
}

export function useHandoffReject() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const reject = useCallback(
    async (id: string, body: RejectHandoffDto): Promise<Readonly<HandoffDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/handoffs/{id}/reject', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '拒绝移交失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, reject };
}

// ============================================================================
// Source Hooks
// ============================================================================

export function useSourceList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (customerId: string): Promise<readonly SourceDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/crm/customers/{customerId}/sources',
          { params: { path: { customerId } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取来源记录失败'));
          return null;
        }
        // Issue #341: see ensureCrmListArray for the wrapper/array contract.
        const guarded = ensureCrmListArray<SourceDto>(data, {
          message: '来源记录响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useSourceCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (customerId: string, body: CreateSourceDto): Promise<Readonly<SourceDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/crm/customers/{customerId}/sources',
          { params: { path: { customerId } }, body },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建来源记录失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

// ============================================================================
// Assignment Hooks
// ============================================================================

export function useAssignment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const get = useCallback(
    async (customerId: string): Promise<Readonly<AssignmentDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/crm/customers/{customerId}/assignment',
          { params: { path: { customerId } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取分配信息失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const upsert = useCallback(
    async (customerId: string, body: UpsertAssignmentDto): Promise<Readonly<AssignmentDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PUT(
          '/api/v1/crm/customers/{customerId}/assignment',
          { params: { path: { customerId } }, body },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '保存分配信息失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, get, upsert };
}

// ============================================================================
// Tag Hooks
// ============================================================================

export function useTagList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (customerId: string): Promise<readonly TagDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/crm/customers/{customerId}/tags',
          { params: { path: { customerId } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取标签失败'));
          return null;
        }
        // Issue #341: see ensureCrmListArray for the wrapper/array contract.
        const guarded = ensureCrmListArray<TagDto>(data, {
          message: '标签响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useTagAdd() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const add = useCallback(
    async (customerId: string, body: AddTagDto): Promise<Readonly<TagDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/crm/customers/{customerId}/tags',
          { params: { path: { customerId } }, body },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '添加标签失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, add };
}

export function useTagRemove() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const remove = useCallback(
    async (customerId: string, id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.DELETE(
          '/api/v1/crm/customers/{customerId}/tags/{id}',
          { params: { path: { customerId, id } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '删除标签失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, remove };
}

// ============================================================================
// ChannelPartner Hooks
// ============================================================================

export function useChannelPartnerList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      status?: ChannelPartnerStatus;
    }): Promise<CrmListResult<ChannelPartnerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/channel-partners', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取渠道合作方列表失败'));
          return null;
        }
        const guarded = ensureCrmListShape<ChannelPartnerDto>(data, {
          message: '渠道合作方列表响应格式异常，请刷新或联系管理员',
          code: 'INVALID_LIST_BODY',
        });
        if (!guarded.ok) {
          setError(guarded.error);
          return null;
        }
        return guarded.value;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useChannelPartnerDetail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (id: string): Promise<Readonly<ChannelPartnerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/channel-partners/{id}', {
          params: { path: { id } },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取渠道合作方详情失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useChannelPartnerCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (body: CreateChannelPartnerDto): Promise<Readonly<ChannelPartnerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/channel-partners', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建渠道合作方失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

export function useChannelPartnerUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const update = useCallback(
    async (id: string, body: UpdateChannelPartnerDto): Promise<Readonly<ChannelPartnerDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/channel-partners/{id}', {
          params: { path: { id } },
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '更新渠道合作方失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, update };
}

// ============================================================================
// CustomerAccount Hooks (Issue #96)
// ============================================================================

// Re-export CustomerAccount labels so consumers can import from one place.
export type { CustomerAccountStatus } from './crmLabels';
export { CUSTOMER_ACCOUNT_STATUS_LABELS } from './crmLabels';

export type CustomerAccountDto = ApiComponents['schemas']['CustomerAccountDto'];
export type CreateCustomerAccountDto = ApiComponents['schemas']['CreateCustomerAccountDto'];
export type ChangeCustomerAccountStatusDto = ApiComponents['schemas']['ChangeCustomerAccountStatusDto'];

export function useCustomerAccountByCustomerId() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const fetch = useCallback(
    async (customerId: string): Promise<{ data: Readonly<CustomerAccountDto> | null; isNotFound: boolean }> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/crm/customer-accounts/by-customer/{customerId}',
          { params: { path: { customerId } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取客户账号失败'));
          return { data: null, isNotFound: false };
        }
        return { data: data ?? null, isNotFound: false };
      } catch (err) {
        const apiError = thrownToApiError(err);
        setError(apiError);
        return { data: null, isNotFound: apiError.status === 404 };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}
export function useCustomerAccountCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const create = useCallback(
    async (body: CreateCustomerAccountDto): Promise<Readonly<CustomerAccountDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/customer-accounts', { body });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建客户账号失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, create };
}

export function useCustomerAccountChangeStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const changeStatus = useCallback(
    async (id: string, body: ChangeCustomerAccountStatusDto): Promise<Readonly<CustomerAccountDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/crm/customer-accounts/{id}/status',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '变更账号状态失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, changeStatus };
}

export function useCustomerAccountSoftDelete() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CrmApiError | null>(null);

  const softDelete = useCallback(
    async (id: string): Promise<Readonly<CustomerAccountDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/crm/customer-accounts/{id}/delete',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '删除客户账号失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, softDelete };
}
